export enum LocalCheckoutScope {
  MINE = 'MINE',
  MY_TEAMS = 'MY_TEAMS',
  ALL_USERS = 'ALL_USERS'
}

export enum LocalCheckoutWarning {
  NONE = 'NONE',
  DIRTY = 'DIRTY'
}

export type LocalCheckoutUser = {
  uid: string;
  name: string;
  email: string;
};

export type LocalCheckoutDetail = {
  uncommittedFileCount: number | null;
  uncommittedFiles: string[];
  sharedWithOtherUsers: number | null;
  sizeOnDiskBytes: number | null;
  lastCommittedAt: string | null;
  lastCommitAuthor: string | null;
  warning: LocalCheckoutWarning;
  warningText: string;
  error: string | null;
};

export type LocalCheckout = {
  userRepositoryUid: string;
  branch: string;
  isMainBranch: boolean;
  isActive: boolean;
  isDeletable: boolean;
  notDeletableReason: string | null;
  user: LocalCheckoutUser;
  detail: LocalCheckoutDetail | null;
};

export type LocalCheckoutList = {
  checkouts: LocalCheckout[];
  totalCount: number;
  unusedCount: number;
  unusedAfterDays: number;
};

export type GitBranchesTabResponse = {
  localGitCheckouts: LocalCheckoutList;
};

export type LocalCheckoutDeletion = {
  userRepositoryUid: string;
  branch: string;
  deleted: boolean;
  reason: string | null;
};

export type LocalCheckoutDeletionResult = {
  requested: number;
  deleted: number;
  skipped: number;
  results: LocalCheckoutDeletion[];
};

export type DeleteGitBranchesResponse = {
  deleteLocalGitCheckouts: LocalCheckoutDeletionResult;
};

export type GetGitBranchesTabParams = {
  projectUid: string;
  scope?: LocalCheckoutScope;
  offset?: number;
  limit?: number;
  includeSizeOnDisk?: boolean;
  unusedAfterDays?: number;
};

export type DeleteGitBranchesParams = {
  userRepositoryUids: string[];
  scope?: LocalCheckoutScope;
};

export type BranchTabState = {
  state: {
    summary: {
      totalCount: number | undefined;
      unusedCount: number | undefined;
      unusedAfterDays: number;
      storageUsedBytes: number | undefined;
    };
    checkouts: LocalCheckout[];
    selectedIds: string[];
    loading: boolean;
    error: string | undefined;
  };
  actions: {
    refresh: () => Promise<void>;
    deleteCheckouts: (variables: DeleteGitBranchesParams) => Promise<LocalCheckoutDeletionResult>;
    toggleCheckout: (userRepositoryUid: string) => void;
    toggleSelectAll: () => void;
    clearSelection: () => void;
  };
};

export enum BranchStatus {
  Active = 'Active',
  Uncommitted = 'Uncommitted'
}

export type BranchStatusItem = { type: BranchStatus.Active } | { type: BranchStatus.Uncommitted; fileCount: number };

const UNVERIFIABLE_CHECKOUT_ERRORS = ['Could not open the git repository', 'Could not read the working tree status'];

export function isCheckoutSelectable(checkout: LocalCheckout) {
  if (!checkout.isDeletable) {
    return false;
  }
  const error = checkout.detail?.error;
  return !error || !UNVERIFIABLE_CHECKOUT_ERRORS.includes(error);
}

export function getCheckoutDisabledReason(checkout: LocalCheckout) {
  if (!checkout.isDeletable) {
    return checkout.notDeletableReason ?? undefined;
  }
  const error = checkout.detail?.error;
  if (error && UNVERIFIABLE_CHECKOUT_ERRORS.includes(error)) {
    return error;
  }
  return undefined;
}

export function getCheckoutStatuses(checkout: LocalCheckout): BranchStatusItem[] {
  const statuses: BranchStatusItem[] = [];
  if (checkout.isActive) {
    statuses.push({ type: BranchStatus.Active });
  }
  if (checkout.detail?.warning === LocalCheckoutWarning.DIRTY) {
    statuses.push({ type: BranchStatus.Uncommitted, fileCount: checkout.detail.uncommittedFileCount ?? 0 });
  }
  return statuses;
}
